import { useState } from 'react';
import { useLinkiRewrite } from '../../../api/linki.api';

/**
 * Rewrite / Shorten / More direct / Match tone actions on a text block.
 *
 * All four call the existing /linki/rewrite endpoint with a different
 * `instruction` string. The suggestion is NEVER auto-applied — the
 * recruiter must press Accept on the diff panel below the buttons.
 *
 * `currentText` is plain text for headings (`.props.text`) or raw HTML
 * for paragraphs (`.props.html`); the same rewrite endpoint handles both
 * — formatting passes through.
 */

const ACTIONS: { id: string; label: string; instruction: (tone: string) => string }[] = [
  {
    id: 'rewrite',
    label: 'Rewrite',
    instruction: () =>
      'Rewrite this while keeping the same meaning. Keep merge tokens like {{first_name}} exactly as-is.',
  },
  {
    id: 'shorten',
    label: 'Shorten',
    instruction: () => 'Shorten this to about half its length. Keep merge tokens exactly as-is.',
  },
  {
    id: 'more_direct',
    label: 'More direct',
    instruction: () => 'Rewrite this to be more direct and action-oriented. Keep merge tokens exactly as-is.',
  },
  {
    id: 'match_tone',
    label: 'Match tone',
    instruction: (tone: string) =>
      `Rewrite this to match a ${tone} tone. Keep merge tokens exactly as-is.`,
  },
];

export interface LinkiActionsProps {
  currentText: string;
  /** The org's configured Linki tone, e.g. 'professional'. Used by the
   *  "Match tone" instruction. Defaults to 'professional'. */
  orgTone?: string;
  /** Called when the recruiter presses Accept on a suggestion. */
  onAccept: (suggestion: string) => void;
}

export function LinkiActions({ currentText, orgTone = 'professional', onAccept }: LinkiActionsProps) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const rewrite = useLinkiRewrite();

  const runAction = async (id: string, instruction: string) => {
    setBusyAction(id);
    try {
      const result = await rewrite.mutateAsync({ current_text: currentText, instruction });
      setSuggestion(result.text);
    } catch {
      setSuggestion(null);
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <section
      aria-label="Linki AI actions"
      style={{
        marginTop: 12,
        padding: 12,
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 8,
        background: 'var(--color-surface-muted, #f7f8fc)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted, #6b6b7d)' }}>
          Linki ✨
        </strong>
      </header>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {ACTIONS.map((a) => (
          <button
            key={a.id}
            type="button"
            data-testid={`linki-${a.id}`}
            disabled={busyAction !== null}
            onClick={() => runAction(a.id, a.instruction(orgTone))}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid var(--color-border-default, #ececf2)',
              borderRadius: 6,
              background: '#fff',
              color: 'var(--color-text-secondary, #2b2b3c)',
              cursor: busyAction !== null ? 'wait' : 'pointer',
              opacity: busyAction !== null && busyAction !== a.id ? 0.5 : 1,
            }}
          >
            {busyAction === a.id ? '…' : a.label}
          </button>
        ))}
      </div>

      {suggestion !== null && (
        <div
          data-testid="linki-diff"
          style={{
            marginTop: 10,
            padding: 10,
            background: '#fff',
            border: '1px dashed var(--color-border-default, #ececf2)',
            borderRadius: 6,
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--color-text-primary, #2b2b3c)',
            whiteSpace: 'pre-wrap',
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9aab', textTransform: 'uppercase', letterSpacing: 0.04 }}>Original</div>
            <div style={{ color: 'var(--color-text-muted, #6b6b7d)', textDecoration: 'line-through' }}>{currentText}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-brand-blue, #3b5bdb)', textTransform: 'uppercase', letterSpacing: 0.04 }}>Suggestion</div>
            <div data-testid="linki-suggestion">{suggestion}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              data-testid="linki-accept"
              onClick={() => {
                onAccept(suggestion);
                setSuggestion(null);
              }}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 600,
                border: 'none',
                borderRadius: 6,
                background: 'var(--color-action-primary, #1e1b4b)',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Accept
            </button>
            <button
              type="button"
              data-testid="linki-discard"
              onClick={() => setSuggestion(null)}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 600,
                border: '1px solid var(--color-border-default, #ececf2)',
                borderRadius: 6,
                background: '#fff',
                color: 'var(--color-text-muted, #6b6b7d)',
                cursor: 'pointer',
              }}
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
